import { z } from "zod";
import { QuestionType, ScoringMode, PointsMode } from "../state/phases.js";
import { normalizeTypedAnswer } from "../scoring/engine.js";
/**
 * The shape of a quiz import file.
 *
 * Written by hand as often as it is generated, so it takes plain values where
 * the database takes rows: answers are strings rather than `{ normalizedValue }`
 * objects, and an option is correct only if it says so. Nothing carries an id —
 * ids belong to the rows the import creates, not to the file.
 *
 * The per-type rules match `validateForPublish`, so anything that imports
 * cleanly can also be published without further editing.
 */
const MAX_QUESTIONS = 100;
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 6;
export const QuizImportOptionSchema = z.object({
    label: z.string().trim().min(1).max(200),
    isCorrect: z.boolean().default(false),
});
/** Accepted answers are normalized here for the same reason the API does it. */
const AcceptedAnswerInputSchema = z
    .array(z.string().max(200))
    .transform((answers) => {
    const seen = new Set();
    const normalized = [];
    for (const answer of answers) {
        const value = normalizeTypedAnswer(answer);
        if (!value || seen.has(value))
            continue;
        seen.add(value);
        normalized.push(value);
    }
    return normalized;
});
const baseQuestion = {
    prompt: z.string().trim().max(2000).optional(),
    timerSeconds: z.number().int().min(5).max(240).optional(),
    scoringMode: z.nativeEnum(ScoringMode).default(ScoringMode.CLASSIC),
    pointsMode: z.nativeEnum(PointsMode).default(PointsMode.STANDARD),
};
const optionList = z.array(QuizImportOptionSchema).min(MIN_OPTIONS).max(MAX_OPTIONS);
const SingleChoiceImportSchema = z.object({
    ...baseQuestion,
    type: z.literal(QuestionType.QUIZ_SINGLE),
    prompt: z.string().trim().min(1).max(2000),
    options: optionList.refine((opts) => opts.filter((o) => o.isCorrect).length === 1, "A single-answer question needs exactly one correct option"),
});
const MultiChoiceImportSchema = z.object({
    ...baseQuestion,
    type: z.literal(QuestionType.QUIZ_MULTI),
    prompt: z.string().trim().min(1).max(2000),
    options: optionList.refine((opts) => opts.some((o) => o.isCorrect), "A multiple-answer question needs at least one correct option"),
});
const TrueFalseImportSchema = z.object({
    ...baseQuestion,
    type: z.literal(QuestionType.TRUE_FALSE),
    prompt: z.string().trim().min(1).max(2000),
    options: z
        .array(QuizImportOptionSchema)
        .length(2)
        .refine((opts) => opts.filter((o) => o.isCorrect).length === 1, "A true or false question needs exactly one correct option"),
});
const TypeAnswerImportSchema = z.object({
    ...baseQuestion,
    type: z.literal(QuestionType.TYPE_ANSWER),
    prompt: z.string().trim().min(1).max(2000),
    acceptedAnswers: AcceptedAnswerInputSchema.refine((answers) => answers.length > 0, "A type-answer question needs at least one accepted answer"),
});
const SlideImportSchema = z.object({
    ...baseQuestion,
    type: z.literal(QuestionType.SLIDE),
});
export const QuizImportQuestionSchema = z.discriminatedUnion("type", [
    SingleChoiceImportSchema,
    MultiChoiceImportSchema,
    TrueFalseImportSchema,
    TypeAnswerImportSchema,
    SlideImportSchema,
]);
export const QuizImportSchema = z.object({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1000).optional(),
    questions: z.array(QuizImportQuestionSchema).min(1).max(MAX_QUESTIONS),
});
/** Timer used when a question does not name one, matching the database default. */
export const IMPORT_DEFAULT_TIMER_SECONDS = 30;
//# sourceMappingURL=quiz-import.js.map